import { useTheme } from '@fluentui/react';
import { IPActionButton } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { ReactNode } from 'react';

export interface IFilterSwitchOption<TKey extends string = string> {
  key: TKey;
  label: ReactNode;
}

interface IFilterSwitchProps<TKey extends string = string> {
  options: readonly IFilterSwitchOption<TKey>[];
  selectedKey: TKey;
  onChange: (key: TKey) => void;
  /** Stops click propagation on each option (useful inside menus/callouts). */
  stopPropagation?: boolean;
}

/**
 * Pill-shaped segmented control used to switch between mutually-exclusive filter values
 * (e.g. "All / My / Shared / Template" view chooser).
 *
 * Colors are sourced from theme tokens that Fluent swaps per active theme, so no manual
 * dark-mode branching is needed:
 *  - Selected: theme.palette.themePrimary (accent) + theme.palette.white text.
 *  - Idle: theme.semanticColors.buttonBackground + buttonBorder + buttonText (UI affordance tokens).
 */
export function FilterSwitch<TKey extends string = string>({
  options,
  selectedKey,
  onChange,
  stopPropagation,
}: IFilterSwitchProps<TKey>) {
  // useTheme() reads the <ThemeProvider> context so the tokens below are the
  // active theme's. getTheme() returns the default light theme in both modes.
  const theme = useTheme();

  const selectedBg = theme.palette.themePrimary;
  const selectedBorder = theme.palette.themePrimary;
  const selectedText = theme.palette.white;
  const idleBg = theme.semanticColors.buttonBackground;
  const idleBorder = theme.semanticColors.buttonBorder;
  const idleText = theme.semanticColors.buttonText;

  return (
    <IPStack horizontal wrap tokens={globalStackTokensGapExtraSmall}>
      {options.map((option) => {
        const isSelected = option.key === selectedKey;

        return (
          <IPActionButton
            key={option.key}
            onClick={(ev) => {
              if (stopPropagation) ev.stopPropagation();
              onChange(option.key);
            }}
            styles={{
              root: {
                border: `1px solid ${isSelected ? selectedBorder : idleBorder}`,
                backgroundColor: isSelected ? selectedBg : idleBg,
                color: isSelected ? selectedText : idleText,
                borderRadius: 16,
                padding: '4px 10px',
                minWidth: 0,
                height: 28,
              },
              rootHovered: {
                backgroundColor: isSelected ? selectedBg : idleBg,
                color: isSelected ? selectedText : idleText,
                borderColor: isSelected ? selectedBorder : idleBorder,
              },
              rootPressed: {
                backgroundColor: isSelected ? selectedBg : idleBg,
                color: isSelected ? selectedText : idleText,
              },
              label: { color: isSelected ? selectedText : idleText, fontWeight: isSelected ? 600 : 400 },
            }}
          >
            {option.label}
          </IPActionButton>
        );
      })}
    </IPStack>
  );
}
